import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, LayoutGrid, List, Edit3, Trash2, Volume2, Users, Mic } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VOICE_LABELS = {
  alloy: "Alloy", echo: "Echo", fable: "Fable",
  onyx: "Onyx", nova: "Nova", shimmer: "Shimmer"
};

export default function SeriesBible() {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seriesFilter, setSeriesFilter] = useState("the-red-ink-chronicles");
  const [viewMode, setViewMode] = useState("gallery");
  const { isDark } = useTheme();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();

  const { mainCast, supportingCast } = useMemo(() => {
    const main = characters.filter(c => c.preview_image_url || c.preview_image || c.image_url);
    const supporting = characters.filter(c => !c.preview_image_url && !c.preview_image && !c.image_url);
    return { mainCast: main, supportingCast: supporting };
  }, [characters]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/characters?series=${seriesFilter}`)
      .then(res => setCharacters(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [seriesFilter]);

  const deleteChar = async (id) => {
    if (!window.confirm("Delete this character?")) return;
    try { await axios.delete(`${API}/characters/${id}`); setCharacters(prev => prev.filter(c => c.id !== id)); }
    catch (e) { alert("Delete failed"); }
  };

  const speakName = (name, pronunciation) => {
    const text = pronunciation || name;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.85;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className={`min-h-screen pt-24 pb-20 px-6 ${isDark ? "" : "text-gray-900"}`} data-testid="series-bible-page">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-8">
          <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-2">WORLD BUILDING</p>
          <h1 className="font-cinzel text-4xl md:text-5xl font-bold mb-3">Series Bible</h1>
          <p className="text-gray-400">The definitive cast reference for your audio dramas</p>
        </header>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex gap-2" data-testid="bible-series-selector">
            {[
              { slug: "the-red-ink-chronicles", label: "TRIC", full: "The Red Ink Chronicles" },
              { slug: "the-notarys-shadow", label: "TNS", full: "The Notary's Shadow" },
              { slug: "signed-sealed-betrayed", label: "SSB", full: "Signed, Sealed, Betrayed" }
            ].map(s => (
              <button key={s.slug} onClick={() => setSeriesFilter(s.slug)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${seriesFilter === s.slug ? "bg-red-600 text-white" : "bg-white/5 text-gray-400 hover:bg-white/10"}`}
                data-testid={`bible-series-${s.slug}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-white/5 rounded-lg p-1" data-testid="view-mode-toggle">
              <button onClick={() => setViewMode("gallery")}
                className={`p-2 rounded ${viewMode === "gallery" ? "bg-red-600 text-white" : "text-gray-400 hover:text-white"}`}
                data-testid="view-gallery-btn"><LayoutGrid size={16} /></button>
              <button onClick={() => setViewMode("list")}
                className={`p-2 rounded ${viewMode === "list" ? "bg-red-600 text-white" : "text-gray-400 hover:text-white"}`}
                data-testid="view-list-btn"><List size={16} /></button>
            </div>
            {isAdmin && (
              <button onClick={() => navigate("/characters/new")}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg transition-all"
                data-testid="create-character-btn">
                <Plus size={16} /> New Character
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-16"><div className="w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full animate-spin" /></div>
        )}

        {!loading && characters.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <p className="text-lg mb-2">No characters yet</p>
            <p className="text-sm">Create your first character to populate the series bible.</p>
          </div>
        )}

        {/* MAIN CAST HEADER */}
        {!loading && mainCast.length > 0 && (
          <div className="flex items-center gap-3 mb-5" data-testid="main-cast-header">
            <Users size={18} className="text-red-500" />
            <h2 className="font-cinzel text-lg font-bold text-white">Main Cast</h2>
            <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">{mainCast.length}</span>
          </div>
        )}

        {/* GALLERY VIEW — Main Cast */}
        {!loading && viewMode === "gallery" && mainCast.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="character-gallery">
            {mainCast.map(char => {
              const rawImage = char.preview_image_url || char.preview_image || char.image_url;
              const imageUrl = rawImage
                ? (rawImage.startsWith("http") ? rawImage : `${BACKEND_URL}${rawImage}`)
                : null;
              return (
                <div key={char.id}
                  className="group relative rounded-xl overflow-hidden border border-white/10 bg-[#0a0a0a] hover:border-red-500/40 transition-all hover:scale-[1.02] cursor-pointer"
                  onClick={() => isAdmin && navigate(`/characters/${char.id}`)}
                  data-testid={`gallery-char-${char.id}`}>
                  <div className="aspect-[3/4] bg-gradient-to-b from-gray-900 to-black">
                    {imageUrl ? (
                      <img src={imageUrl} alt={char.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-5xl font-cinzel text-gray-700 opacity-50">{char.name?.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-4 pt-12">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-cinzel font-bold text-sm text-white truncate">{char.name}</h3>
                      {char.pronunciation && (
                        <button onClick={(e) => { e.stopPropagation(); speakName(char.name, char.pronunciation); }}
                          className="p-1 rounded bg-white/10 hover:bg-red-600/40 transition-colors flex-shrink-0"
                          data-testid={`speak-${char.id}`} title={`Say: "${char.pronunciation}"`}>
                          <Volume2 size={12} />
                        </button>
                      )}
                    </div>
                    {char.pronunciation && (
                      <p className="text-[10px] text-amber-400/80 font-mono mb-1">/{char.pronunciation}/</p>
                    )}
                    {char.nickname && <p className="text-[10px] text-gray-400 italic mb-1">"{char.nickname}"</p>}
                    <p className="text-[10px] text-gray-500 line-clamp-2">{char.description}</p>
                    <div className="flex gap-1 mt-2">
                      {char.gender && <span className="text-[9px] px-1.5 py-0.5 bg-white/10 rounded text-gray-400">{char.gender}</span>}
                      {char.age_range && <span className="text-[9px] px-1.5 py-0.5 bg-white/10 rounded text-gray-400">{char.age_range}</span>}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/characters/${char.id}`); }}
                        className="p-1.5 bg-black/70 rounded hover:bg-amber-600 transition-colors" data-testid={`edit-char-${char.id}`}>
                        <Edit3 size={12} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteChar(char.id); }}
                        className="p-1.5 bg-black/70 rounded hover:bg-red-600 transition-colors" data-testid={`delete-char-${char.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* LIST VIEW — Main Cast */}
        {!loading && viewMode === "list" && mainCast.length > 0 && (
          <div className="space-y-3" data-testid="character-list">
            {mainCast.map(char => (
              <div key={char.id}
                className={`p-5 rounded-xl border transition-all ${isAdmin ? "cursor-pointer" : ""} ${isDark ? "bg-[#0a0a0a] border-white/10 hover:border-red-500/30" : "bg-white border-gray-200 hover:border-red-400"}`}
                onClick={() => isAdmin && navigate(`/characters/${char.id}`)}
                data-testid={`list-char-${char.id}`}>
                <div className="flex items-start gap-5">
                  <div className="w-16 h-16 rounded-lg bg-gray-900 flex-shrink-0 overflow-hidden">
                    {(() => {
                      const raw = char.preview_image_url || char.preview_image || char.image_url;
                      const url = raw ? (raw.startsWith("http") ? raw : `${BACKEND_URL}${raw}`) : null;
                      return url ? (
                        <img src={url} alt={char.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl font-cinzel text-gray-600">{char.name?.charAt(0)}</div>
                      );
                    })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-cinzel font-bold">{char.name}</h3>
                      {char.pronunciation && (
                        <>
                          <span className="text-xs text-amber-400/70 font-mono">/{char.pronunciation}/</span>
                          <button onClick={(e) => { e.stopPropagation(); speakName(char.name, char.pronunciation); }}
                            className="p-1 rounded hover:bg-white/10 transition-colors" data-testid={`speak-list-${char.id}`}>
                            <Volume2 size={12} className="text-gray-500" />
                          </button>
                        </>
                      )}
                      {char.nickname && <span className="text-xs text-gray-500 italic">"{char.nickname}"</span>}
                    </div>
                    <p className={`text-sm mb-2 line-clamp-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{char.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {char.gender && <span className="text-[10px] px-2 py-0.5 bg-white/5 border border-white/10 rounded text-gray-400">{char.gender}</span>}
                      {char.age_range && <span className="text-[10px] px-2 py-0.5 bg-white/5 border border-white/10 rounded text-gray-400">{char.age_range}</span>}
                      {char.ai_voice && <span className="text-[10px] px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400">Voice: {char.ai_voice}</span>}
                      {char.traits?.map((t, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded text-amber-400">{t}</span>
                      ))}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/characters/${char.id}`); }}
                        className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid={`edit-list-${char.id}`}>
                        <Edit3 size={16} className="text-gray-400" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteChar(char.id); }}
                        className="p-2 rounded-lg hover:bg-red-500/20 transition-colors" data-testid={`delete-list-${char.id}`}>
                        <Trash2 size={16} className="text-gray-400" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SUPPORTING CAST SECTION */}
        {!loading && supportingCast.length > 0 && (
          <div className="mt-12" data-testid="supporting-cast-section">
            <div className="flex items-center gap-3 mb-5">
              <Mic size={18} className="text-red-500/70" />
              <h2 className="font-cinzel text-lg font-bold text-white/80">Supporting Cast</h2>
              <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">{supportingCast.length}</span>
              <span className="text-[10px] text-gray-600 ml-1">Voice-only characters</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="supporting-cast-grid">
              {supportingCast.map(char => (
                <div key={char.id}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${isAdmin ? "cursor-pointer hover:border-red-500/30" : ""} ${isDark ? "bg-[#0a0a0a]/60 border-white/5 hover:bg-[#0a0a0a]" : "bg-white/50 border-gray-200"}`}
                  onClick={() => isAdmin && navigate(`/characters/${char.id}`)}
                  data-testid={`supporting-char-${char.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-cinzel text-gray-500">{char.name?.charAt(0)}</span>
                    </div>
                    <span className="font-medium text-sm text-white/90 truncate">{char.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    {char.ai_voice && (
                      <span className="text-[10px] px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400 font-mono">
                        {VOICE_LABELS[char.ai_voice] || char.ai_voice}
                      </span>
                    )}
                    {isAdmin && (
                      <button onClick={(e) => { e.stopPropagation(); deleteChar(char.id); }}
                        className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                        data-testid={`delete-supporting-${char.id}`}>
                        <Trash2 size={12} className="text-gray-500" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
